<template>
  <div :id="`nxt-app${customId ? ` ${customId}` : ''}`"
       :class="[$setClasses, 'custom', currentStep ? `--${currentStep.id}` : '']">
    <template v-if="!partnerLoading && !partnerError">
      <div v-if="headerConfig?.content"
           :class="$vPrefix('header')"
           v-html="headerConfig.content"/>
      <transition :name="$vPrefix('fade')"
                  mode="out-in">
        <multi-step-form :class="$vClass('content')"/>
      </transition>
    </template>
    <div v-else-if="partnerLoading"
         :class="$vClass('loading')">
      <nxt-core-loader :text="$t('base.loading')"
                       size-loader="--size-small"/>
    </div>
    <div v-else :class="$vClass('error')">
      <nxt-core-text-content :class="$vClass('error__title')" tag="h1">
        {{ $t('base.messageError.form') }}
      </nxt-core-text-content>
      <nxt-core-text-content :class="$vClass('error__subtitle')">
        {{ $t('base.contactAdmin') }}
      </nxt-core-text-content>
      <nxt-core-link :class="[$vClass('error__link'), 'nxt-button nxt-button__primary']"
                     :to="$t('base.phone.link')"
                     is-external
                     target="_blank">
        {{ $t('base.phone.text') }}
      </nxt-core-link>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import { usePartnerStore } from './store/partnerStore.ts';
import OnResize from "@nextories/vue-core-components/src/utils/mixins/OnResize.ts";
import NxtApp from '@nextories/vue-core-components/src/components/templates/NxtCoreApp.vue';

export default {
  extends: NxtApp,
  name: 'App',
  nameClassStyle: 'app',
  mixins: [OnResize],
  computed: {
    ...mapState(usePartnerStore, [
      'configId',
      'partnerId',
      'customId',
      'partnerStyle',
      'partnerLoading',
      'currentStepNb',
      'partnerError',
      'headerConfig',
      'currentStep',
    ]),
  },
  watch: {
    partnerStyle(customStyle) {
      const partnerCustom = customStyle;
      const addStyleTag = document.createElement('style');
      addStyleTag.appendChild(document.createTextNode(partnerCustom));
      document.head.appendChild(addStyleTag);
    },
    currentStepNb() {
      this.$helpers.gmaps.removePacContainers();
    },
  },
  created() {
    if (this.configId) {
      this.getPartner({
        partnerId: this.partnerId,
        configId: this.configId,
      });
    }
  },
  methods: {
    ...mapActions(usePartnerStore, [
      'getPartner',
    ]),
  },
};
</script>

<style lang="scss" scoped>
.#{$prefixName}app {
  background-color: var(--nxt-color-white);
  font-size: var(--nxt-font-size-default);
  color: var(--nxt-color-text-primary);
  padding: 0;
  margin: 0 auto;
  width: 100%;
  height: 100%;
  min-height: 90vh;
  max-width: 1280px;
  overflow-x: hidden;

  &__error {
    padding: var(--nxt-space-medium);
    background-color: var(--nxt-color-secondary);
    text-align: center;
    color: var(--nxt-color-white);

    &__title {
      color: var(--nxt-color-primary);
      font-size: var(--nxt-font-size-large);
    }

    &__subtitle {
      color: var(--nxt-color-white);
      font-size: var(--nxt-font-size-default);
    }

    &__link {
      margin: auto;
      height: auto;
      width: 150px;
    }
  }

  :deep(.el-main) {
    scrollbar-width: none;
    -ms-overflow-style: -ms-autohiding-scrollbar;
  }

  ::-webkit-scrollbar {
    width: 0;
    display: none;
    @include breakpoint(tablet) {
      display: unset;
      height: 5px;
    }
  }

  /* Track */
  ::-webkit-scrollbar-track {
    background: var(--nxt-color-light-grey-blue);
    border-radius: var(--nxt-border-radius-base);
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: var(--nxt-color-secondary);
    border-radius: var(--nxt-border-radius-base);
  }

  /* Handlesource bin/activate && python manage.py runserver localhost:9000 on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: var(--nxt-color-secondary);
  }

  :deep(*),
  :deep(svg:not(:root).svg-inline--fa),
  :deep(svg:not(:host).svg-inline--fa) {
    box-sizing: border-box;
    user-select: none;
  }

  :deep(input),
  :deep(textarea) {
    user-select: text;
  }

  :deep(.el-date-editor) {
    height: 40px;
  }

  :deep(.el-input__inner::placeholder),
  :deep(.el-icon.el-input__icon) {
    color: #C0C4CC;
  }

  h2 {
    margin: 0;
    font-weight: var(--nxt-font-weight-bold);
    font-size: var(--nxt-font-size-medium);
  }

  :deep(ul) {
    padding: 0;
  }

  :deep(li) {
    list-style: none;
  }

  &__content {
    height: auto;
  }

  &__loading {
    height: 100%;
    width: 100%;
  }

  :deep(.#{$prefixName}input-select) {
    width: 100%;

    .el-select__tags-text {
      color: var(--nxt-color-grey-blue);
    }

    .el-tag__close.el-icon-close {
      color: var(--nxt-color-white);
      background-color: var(--nxt-color-primary);
      font-weight: var(--nxt-font-weight-bold);
    }
  }

  :deep(.#{$prefixName}inventory) {
    .#{$prefixName}--hidden {
      visibility: hidden;
    }

    .#{$prefixName}inventory__top {
      height: 75px;
      @include breakpoint(mobileWithHeight) {
        height: 90px;
      }
    }

    .#{$prefixName}inventory__label {
      .#{$prefixName}input-label {
        margin-top: 5px;
        margin-bottom: 15px;
      }
    }

    .#{$prefixName}inventory__header {
      display: flex;
      grid-template-columns: auto auto;
      place-items: baseline;

      &__add-room {
        display: inline-block;
      }

      &__room-item {
        padding: 5px 10px;
        margin: 0 7px;
        background-color: var(--nxt-color-light-grey-blue);
        border-radius: var(--nxt-border-radius-base);

        &--active {
          background-color: var(--nxt-color-secondary);
          color: var(--nxt-color-white);
          border-radius: var(--nxt-border-radius-base);
        }

        &--edit {
          background: white;
          padding: 0;
        }
      }

      &__icon {
        font-size: var(--nxt-font-size-large);
      }

      .el-input__wrapper {
        .el-input__inner {
          border: 0 !important;
        }
      }
    }

    .#{$prefixName}inventory__room-selector {
      margin: 10px 0;
      display: grid;
      grid-template-columns: 1fr auto auto;
      align-items: center;

      &__burger_menu {
        margin: 0 .8em;
        cursor: pointer;
      }
    }

    .#{$prefixName}inventory__room-delete.#{$prefixName}dialog {
      .#{$prefixName}dialog__content__header {
        position: inherit !important;
      }
    }

    .#{$prefixName}inline-selector {
      &__list {
        overflow-x: hidden;

        &__item {
          margin-bottom: 0;
        }
      }
    }

    @include breakpoint(mobileWithHeight) {
      .#{$prefixName}inventory__room-delete .#{$prefixName}dialog__content {
        height: auto;
      }
    }

    .#{$prefixName}furniture-selector {
      overflow: hidden;
      @include breakpoint(mobileWithHeight) {
        border: 1px solid var(--nxt-color-text-secondary);
        border-radius: var(--nxt-border-radius-base);
        height: fit-content;
        &__search-bar {
          border-top: none;
          grid-template-columns: minmax(100px, 500px) auto;
          margin: 0;
          border-radius: var(--nxt-border-radius-base) var(--nxt-border-radius-base) 0 0;
          height: 60px;
        }
      }

      &-body {
        ::-webkit-scrollbar {
          @include breakpoint(mobileWithHeight) {
            display: block !important;
            height: 5px;
            background: var(--nxt-color-secondary);
            border-radius: var(--nxt-border-radius-base);
          }
        }

        & {
          display: block;
        }

        &__header {
          overflow: hidden;
          height: 60px;
          margin: 0;
          width: auto;
          @include breakpoint(desktop) {
            padding: 10px;
          }
          @include breakpoint(mobileWithHeight) {
            height: 60px;
          }

          .#{$prefixName}selector-arrow__right {
            padding-right: 0;
          }
        }

        &__furniture-list {
          scrollbar-width: none;
          -ms-overflow-style: -ms-autohiding-scrollbar;
          @include breakpoint(mobileWithHeight) {
            padding-bottom: 0;
            height: calc(95vh - 385px);
            scrollbar-color: var(--nxt-color-secondary) var(--nxt-color-light-grey-blue);
            scrollbar-width: thin;
          }
          @include breakpoint(desktop) {
            grid-template-columns: repeat(auto-fill, 170px);
          }
          @include breakpoint(desktopWithHeight) {
            height: calc(80vh - 385px);
          }

          &::-webkit-scrollbar:vertical {
            width: 5px;
          }

          &__item {
            &.#{$prefixName}furniture-card {
              @include breakpoint(mobileWithHeight) {
                max-width: 160px;
              }
            }
          }
        }
      }
    }
  }

  :deep(.#{$prefixName}furniture-modal__footer__text),
  :deep(.#{$prefixName}inventory__footer__text) {
    display: grid;
    grid-template-columns: auto auto;
    margin: 0;
    background-color: var(--nxt-color-light-grey-blue);
    padding: 0.7rem 0;
    font-size: var(--nxt-font-size-small);
    border-radius: var(--nxt-border-radius-base);

    p {
      justify-self: center;
      margin: 0;
    }

    @include breakpoint(mobileWithHeight) {
      font-size: var(--nxt-font-size-large);
      border-radius: 0;
      border-bottom: {
        right-radius: var(--nxt-border-radius-base);
        left-radius: var(--nxt-border-radius-base);
      }
      height: 45px;
    }
  }

  :deep(.#{$prefixName}field__label) {
    font-size: var(--nxt-font-size-small);

    @include breakpoint(mobileWithHeight) {
      font-size: var(--nxt-font-size-medium);
    }
  }

  .#{$prefixName}fade {
    &-enter-active, &-leave-active {
      transition: opacity 200ms;
    }

    &-enter-from, &-leave-to {
      opacity: 0;
    }
  }

  :deep(.#{$prefixName}dialog) {
    z-index: 9999;
    height: 100%;
  }
}
</style>
